export interface CameraStartOptions {
  facingMode?: "environment" | "user";
  /** Preferred capture resolution; the browser will pick the closest match. */
  width?: number;
  height?: number;
}

/**
 * Wraps `getUserMedia` plus the non-standard-but-widely-supported
 * `MediaTrackConstraints` torch/zoom extensions, so the rest of the SDK
 * doesn't need to know about `applyConstraints` quirks across browsers.
 */
export class CameraController {
  readonly video: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private track: MediaStreamTrack | null = null;

  constructor(video?: HTMLVideoElement) {
    this.video = video ?? document.createElement("video");
    this.video.playsInline = true;
    this.video.muted = true;
    this.video.autoplay = true;
  }

  async start(options: CameraStartOptions = {}): Promise<void> {
    if (this.stream) return;
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: options.facingMode ?? "environment",
        width: { ideal: options.width ?? 1920 },
        height: { ideal: options.height ?? 1080 },
        // Continuous AF keeps close-range barcodes sharp without user intervention.
        advanced: [{ focusMode: "continuous" } as unknown as MediaTrackConstraintSet],
      },
    };
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.track = this.stream.getVideoTracks()[0] ?? null;
    this.video.srcObject = this.stream;
    await this.video.play().catch(() => undefined);
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.track = null;
    this.video.srcObject = null;
  }

  get isActive(): boolean {
    return this.stream !== null;
  }

  /** `deviceId` of the currently active video track, if any — used to mark the active entry in a camera picker. */
  get activeDeviceId(): string | null {
    return this.track?.getSettings().deviceId ?? null;
  }

  get supportsTorch(): boolean {
    const caps = this.track?.getCapabilities?.();
    return Boolean(caps && "torch" in caps);
  }

  async setTorch(on: boolean): Promise<boolean> {
    if (!this.track || !this.supportsTorch) return false;
    try {
      await this.track.applyConstraints({ advanced: [{ torch: on } as unknown as MediaTrackConstraintSet] });
      return true;
    } catch {
      return false;
    }
  }

  async listVideoInputs(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === "videoinput");
  }

  async switchDevice(deviceId: string): Promise<void> {
    const wasActive = this.isActive;
    this.stop();
    if (!wasActive) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { deviceId: { exact: deviceId } },
    });
    this.track = this.stream.getVideoTracks()[0] ?? null;
    this.video.srcObject = this.stream;
    await this.video.play().catch(() => undefined);
  }
}
